<script setup lang="ts">
/**
 * TargetPacingCard — the MERGED monthly sales card.
 *
 * bead lucky-war-etcn.2 merged the ยอดขายเทียบเป้าหมาย hero with the MTD
 * remark; bead lucky-war-tl5y completes the merge — the standalone
 * รายละเอียดยอดขายเดือนนี้ card (SalesWidget) left the homepage and its
 * surviving MTD signal folds in here as one compact discount line.
 *   - hero: achieved ฿ vs target ฿ (chain default ฿300M — PO 2026-08-17,
 *     target-entry UI deferred)
 *   - remark: ยอดขายเดือนนี้ · N บิล · M คน (from /sales/overview MTD)
 *   - discount: ส่วนลดรวมเดือนนี้ ฿X · Y% (absorbed from SalesWidget)
 *   - freshness: ข้อมูลถึง X ส.ค. · อัปเดตล่าสุด Y
 *   - progress bar + pace context + projection (bead lucky-war-t8n0)
 *   - uPlot: cumulative actual vs linear target pace (tl5y pick #1)
 *
 * Removed in tl5y: the "เปลี่ยนจากเดือนก่อน: BBQ x จุดเปอร์เซ็นต์" mix-delta and the
 * ส่วนลดโปรโมชัน footer lines — the ratio now lives only on /weekly and
 * the /branch region band (home PromoImpactPanel removed 2026-08-19), and
 * the mix-share point delta read as unfinished and carried no decision.
 *
 * Brand lens keeps the ยอดขายเดือนนี้ behavior (no target configured).
 */
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import type { SalesMonthlyProgressResponse, SalesTrendResponse, SalesOverviewResponse } from '@/api';
import { fetchSalesMonthlyProgress, fetchSalesTrend, fetchSalesOverview } from '@/api';
import { apiBrand, lensFromQuery, scopeLabel } from '@/lib/brand';
import { cssVar, cssVarRgba } from '@/lib/display';
import { tooltipPlugin, tooltipBaht } from '@/lib/uplotTooltip';
import SalesMixBar from './SalesMixBar.vue';
import ShareButton from '@/components/ShareButton.vue';
import type { KpiCardShareInput, KpiRow } from '@/lib/shareFlex';

const data = ref<SalesMonthlyProgressResponse | null>(null);
const trend = ref<SalesTrendResponse | null>(null);
const salesOv = ref<SalesOverviewResponse | null>(null);
const route = useRoute();
const lens = computed(() => lensFromQuery(route.query.brand));
const loading = ref(false);
const error = ref<string | null>(null);

watch(() => route.query.brand, load, { immediate: true });

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const brand = apiBrand(lens.value);
    const [res, t, ov] = await Promise.all([
      fetchSalesMonthlyProgress(undefined, brand),
      fetchSalesTrend(90, undefined, brand),
      fetchSalesOverview({ range: 'mtd', brand }),
    ]);
    data.value = res; trend.value = t; salesOv.value = ov;
    loading.value = false;
    return;
  } catch {
    // real endpoint not reachable — OK in dev, warn in prod
  }

  // ── Mock fallback (only when real endpoint fails) ──
  console.warn('[MOCK FALLBACK] TargetPacingCard — real endpoint failed, using mock');
  if (import.meta.env.PROD) {
    error.value = 'ไม่สามารถโหลดข้อมูลเป้าหมายรายเดือนได้';
    loading.value = false;
    return;
  }
  data.value = {
    target: 300_000_000,
    target_status: 'ok',
    achieved_mtd: 87_500_000,
    remaining: 212_500_000,
    days_elapsed: 12,
    days_remaining: 19,
    daily_burn_needed: 11_184_211,
    projected_eom: 225_000_000,
    is_on_track: false,
    as_of_date: '2026-08-12',
  };
  loading.value = false;
}

const progressPct = computed(() => {
  if (!data.value || data.value.target == null || data.value.target <= 0) return 0;
  return Math.min(100, (data.value.achieved_mtd / data.value.target) * 100);
});

const projectedPct = computed(() => {
  if (!data.value || data.value.target == null || data.value.target <= 0) return 0;
  return Math.min(100, (data.value.projected_eom / data.value.target) * 100);
});

function fmtBaht(n: number): string {
  return Math.round(n).toLocaleString('th-TH');
}

function fmtPct(n: number): string {
  return n.toFixed(1);
}

// ── MTD remark (absorbed from the retired standalone MTD card) ─────────────
// UTC-stable parse — a bare `new Date(iso+'T00:00:00')` parses as LOCAL time
// and shifts ICT labels back a day.
const THAI_SHORT_MONTHS = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

function isoToThaiDate(iso: string): string {
  const p = iso.split('-').map(Number);
  const d = new Date(Date.UTC(p[0], p[1] - 1, p[2]));
  return `${d.getUTCDate()} ${THAI_SHORT_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear() + 543}`;
}

function rfcToThaiDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Bangkok', // display TZ is ICT regardless of client clock
  }).format(d);
}

// "ยอดขายเดือนนี้ · 98,688 บิล · 244,491 คน · ไม่รวม VAT" — in brand view the
// header already says ยอดขายเดือนนี้, so the prefix is dropped there. The
// "ไม่รวม VAT" suffix discloses that the figure is Base Sales (pre-VAT,
// pre-service-charge, finance Excel convention — bead lucky-war-lfdf).
const mtdRemark = computed(() => {
  const ov = salesOv.value;
  if (!ov || ov.is_empty) return '';
  const parts = [`${ov.bill_count.toLocaleString('th-TH')} บิล`, `${ov.cust_count.toLocaleString('th-TH')} คน`, 'ไม่รวม VAT'];
  const prefix = brandView.value ? '' : 'ยอดขายเดือนนี้ · ';
  return prefix + parts.join(' · ');
});

// "ข้อมูลถึง 16 ส.ค. 2569 · อัปเดตล่าสุด 17 ส.ค. 2569 08:15"
const mtdFreshness = computed(() => {
  const f = salesOv.value?.freshness;
  if (!f) return '';
  const parts: string[] = [];
  if (f.data_through) parts.push(`ข้อมูลถึง ${isoToThaiDate(f.data_through)}`);
  if (f.last_ingest_at) parts.push(`อัปเดตล่าสุด ${rfcToThaiDateTime(f.last_ingest_at)}`);
  return parts.join(' · ');
});

const trackClass = computed(() =>
  data.value?.is_on_track ? 'target-pacing--on-track' : 'target-pacing--off-track',
);
const brandView = computed(() => lens.value !== 'all');
const cardTitle = computed(() => brandView.value
  ? `ยอดขายเดือนนี้ · ${scopeLabel(lens.value)}`
  : 'ยอดขายเดือนนี้เทียบเป้าหมาย · รวมทุกแบรนด์');

// ── LIFF share (bead lucky-war-trko) ─────────────────────────────────────────
function shareInput(): KpiCardShareInput {
  const d = data.value;
  const rows: KpiRow[] = [{ label: 'ทำได้แล้ว (MTD)', value: `฿${fmtBaht(d?.achieved_mtd ?? 0)}` }];
  if (d && !brandView.value && d.target_status === 'ok' && d.target != null) {
    rows.push({ label: 'เป้าหมายเดือนนี้', value: `฿${fmtBaht(d.target)}` });
    rows.push({
      label: 'ความคืบหน้า',
      value: `${fmtPct(progressPct.value)}%`,
      delta: {
        text: `คาดการณ์สิ้นเดือน ฿${fmtBaht(d.projected_eom)} (${fmtPct(projectedPct.value)}%) · ${d.is_on_track ? 'ตามเป้า' : 'ต่ำกว่าเป้า'}`,
        good: d.is_on_track,
      },
    });
  }
  const win = [mtdFreshness.value, mtdRemark.value].filter(Boolean).join(' · ');
  return {
    title: cardTitle.value,
    windowLabel: win,
    rows,
    note: 'ยอดขาย Base Sales · ไม่รวม VAT',
    linkPath: route.fullPath,
  };
}
const perBranch = computed(() => {
  const ov = salesOv.value;
  const branches = ov?.branches_with_sales ?? 0;
  if (!brandView.value || !ov || ov.is_empty || !data.value || branches <= 0) return null;
  return data.value.achieved_mtd / branches;
});
const mix = computed(() => data.value?.sales_mix ?? null);

// ── MTD discount line (absorbed from the retired SalesWidget, tl5y) ────────
const mtdDiscount = computed(() => {
  const ov = salesOv.value;
  if (!ov || ov.is_empty) return null;
  if (!(ov.discount_total > 0)) return null;
  const pct = ov.discount_pct != null ? ` · ${(ov.discount_pct * 100).toFixed(2)}% ของยอดขาย` : '';
  return `ส่วนลดรวมเดือนนี้ ฿${Math.round(ov.discount_total).toLocaleString('th-TH')}${pct}`;
});

// ── uPlot: cumulative actual vs linear target pace (tl5y pick #1) ──────────
// x = day-of-month (ordinal), y = ฿. The pace line runs 0 → target across the
// full month; the actual line is the MTD running sum of trend days and stops
// at as_of. Chain view + configured target + ≥3 MTD days only — no fake lines.
const PLOT_HEIGHT = 150;
const plotHost = ref<HTMLElement | null>(null);
let plot: uPlot | null = null;
let resizeObserver: ResizeObserver | null = null;

const fmtBahtAxis = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `฿${Math.round(v / 1_000_000)}M`;
  if (Math.abs(v) >= 1_000) return `฿${Math.round(v / 1000)}k`;
  return `฿${Math.round(v)}`;
};

const paceSpec = computed<{ days: number[]; actual: (number | null)[]; pace: (number | null)[] } | null>(() => {
  const d = data.value;
  const target = d?.target ?? null;
  if (!d || brandView.value || d.target_status !== 'ok' || target == null || target <= 0) return null;
  const asOf = d.as_of_date;
  const monthPrefix = asOf.slice(0, 7);
  const [y, m] = monthPrefix.split('-').map(Number);
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mtd = (trend.value?.days ?? [])
    .filter((r) => r.date.startsWith(monthPrefix) && r.date <= asOf && r.net_total != null)
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  if (mtd.length < 3) return null;
  const actual: (number | null)[] = new Array(daysInMonth).fill(null);
  let run = 0;
  for (const r of mtd) {
    run += r.net_total ?? 0;
    actual[Number(r.date.slice(8, 10)) - 1] = run;
  }
  const pace: (number | null)[] = Array.from(
    { length: daysInMonth },
    (_, i) => (target * (i + 1)) / daysInMonth,
  );
  return { days: Array.from({ length: daysInMonth }, (_, i) => i + 1), actual, pace };
});

// ── Gap callout (PO 2026-08-19): actual vs pace at as_of, in points of
// target attainment. The two lines run nearly parallel a few % apart — a
// few pixels on a 0..300M scale — so the chart alone hides whether the
// store is ahead or behind (mgmt misread ต่ำกว่าเป้า ✗ as a chart bug).
// UX rule: color carries text, never color alone.
const paceGap = computed<{ label: string; cls: string } | null>(() => {
  const spec = paceSpec.value;
  const d = data.value;
  const target = d?.target ?? null;
  if (!spec || !d || target == null || target <= 0) return null;
  const dayOfAsOf = Number(d.as_of_date.slice(8, 10));
  const daysInMonth = spec.days.length;
  const pacePct = (dayOfAsOf / daysInMonth) * 100;
  const gap = progressPct.value - pacePct;
  if (Math.abs(gap) < 0.1) return { label: 'ความคืบหน้ายอดขายตามจังหวะเป้าหมาย', cls: 'target-pacing__gap--flat' };
  if (gap > 0) return { label: `ความคืบหน้ายอดขายสูงกว่าจังหวะเป้าหมาย ${gap.toFixed(1)} จุดเปอร์เซ็นต์`, cls: 'target-pacing__gap--up' };
  return { label: `ความคืบหน้ายอดขายต่ำกว่าจังหวะเป้าหมาย ${Math.abs(gap).toFixed(1)} จุดเปอร์เซ็นต์`, cls: 'target-pacing__gap--down' };
});

function buildPaceOptions(width: number): uPlot.Options {
  return {
    width,
    height: PLOT_HEIGHT,
    cursor: { drag: { x: false, y: false, setScale: false }, focus: { prox: 12 } },
    legend: { live: false, isolate: false },
    plugins: [
      tooltipPlugin((u, idx) => {
        const month = Number((data.value?.as_of_date ?? '').slice(5, 7));
        const mLabel = THAI_SHORT_MONTHS[month >= 1 && month <= 12 ? month - 1 : 0];
        const lines = [`${idx + 1} ${mLabel}`];
        const actual = u.data[1][idx] as number | null;
        const pace = u.data[2][idx] as number | null;
        if (actual != null) lines.push(`ยอดสะสม ${tooltipBaht(actual)}`);
        if (pace != null) lines.push(`เป้าตามจังหวะ ${tooltipBaht(pace)}`);
        return lines.join('\n');
      }),
    ],
    scales: { x: { time: false } },
    series: [
      { label: 'วันที่' },
      {
        label: 'ยอดสะสม',
        stroke: cssVar('--sales'),
        width: 3,
        // Solid darker fill (PO 2026-08-29): the pale --sales-soft tint was
        // near-invisible against the card — use the sales gold at alpha.
        fill: cssVarRgba('--sales', 0.25),
        spanGaps: false,
        points: { show: false },
        paths: uPlot.paths.spline ? uPlot.paths.spline() : undefined,
      },
      {
        label: 'เป้าตามจังหวะ',
        // Status red (design system: pace marker color) — darker/solid-read
        // than the old brand-red stroke (PO 2026-08-29).
        stroke: cssVar('--color-bad'),
        dash: [2, 3] as number[],
        width: 2.5,
        spanGaps: true,
        points: { show: false },
      },
    ],
    axes: [
      {
        stroke: cssVar('--text-faint'),
        grid: { show: false },
        font: '11px Sarabun, sans-serif',
        values: (_u, vals) => vals.map((v) => (v == null ? '—' : `${Math.round(v)}`)),
      } as uPlot.Axis,
      {
        stroke: cssVar('--text-faint'),
        grid: { stroke: cssVar('--border-hair') },
        font: '11px Sarabun, sans-serif',
        values: (_u, vals) => vals.map((v) => (v == null ? '—' : fmtBahtAxis(v))),
        size: 44,
      } as uPlot.Axis,
    ],
  };
}

function destroyPlot() {
  plot?.destroy();
  plot = null;
}

function syncPlot() {
  const spec = paceSpec.value;
  if (!spec || !plotHost.value) return;
  const arrays = [spec.days, spec.actual, spec.pace] as unknown as uPlot.AlignedData;
  if (plot) {
    plot.setData(arrays);
    return;
  }
  const width = plotHost.value.clientWidth || 600;
  plot = new uPlot(buildPaceOptions(width), arrays, plotHost.value);
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver ??= new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      if (w > 0 && plot) plot.setSize({ width: w, height: PLOT_HEIGHT });
    });
    resizeObserver.observe(plotHost.value);
  }
}

watch([paceSpec, plotHost], async () => {
  await nextTick();
  if (!paceSpec.value || !plotHost.value) {
    destroyPlot();
    return;
  }
  syncPlot();
});

onUnmounted(() => {
  destroyPlot();
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>

<template>
  <!-- Loading -->
  <section v-if="loading" class="target-pacing state-msg" aria-label="กำลังโหลดเป้าหมายรายเดือน">
    <p>กำลังโหลดข้อมูลเป้าหมาย...</p>
  </section>

  <!-- Error (don't block dashboard — show nothing on error) -->
  <template v-else-if="error"></template>

  <!-- Data -->
  <section
    v-else-if="data"
    class="target-pacing"
    :class="trackClass"
    :aria-label="cardTitle"
  >
    <h2 class="card-head">{{ cardTitle }}</h2>
    <ShareButton :build="shareInput" />

    <!-- Big achieved number -->
    <div class="target-pacing__headline">
      <span class="target-pacing__headline-baht">฿{{ fmtBaht(data.achieved_mtd) }}</span>
      <span v-if="!brandView && data.target_status === 'ok'" class="target-pacing__headline-target">เป้าหมาย ฿{{ fmtBaht(data.target ?? 0) }}</span>
    </div>

    <p v-if="perBranch != null" class="target-pacing__mtd-remark target-pacing__per-branch">
      เฉลี่ยต่อสาขา ฿{{ fmtBaht(perBranch) }} · จาก {{ salesOv?.branches_with_sales }} สาขาที่มียอดขาย
    </p>

    <!-- MTD remark (ยอดขายเดือนนี้ demoted here per merge) -->
    <p v-if="mtdRemark" class="target-pacing__mtd-remark">{{ mtdRemark }}</p>
    <p v-if="mtdFreshness" class="target-pacing__freshness">{{ mtdFreshness }}</p>

    <!-- Progress bar -->
    <div v-if="!brandView && data.target_status === 'ok'" class="target-pacing__bar-wrap">
      <div class="target-pacing__bar-track">
        <div
          class="target-pacing__bar-fill"
          :style="{ width: `${progressPct}%` }"
        ></div>
        <!-- Projected marker -->
        <div
          v-if="projectedPct > 0 && Math.abs(projectedPct - progressPct) > 0.5"
          class="target-pacing__bar-proj"
          :style="{ left: `${projectedPct}%` }"
          :title="`คาดการณ์ ฿${fmtBaht(data.projected_eom)}`"
        ></div>
      </div>
      <div class="target-pacing__bar-labels">
           <span>ทำได้ {{ fmtPct(progressPct) }}%</span>
        <span>100%</span>
      </div>
    </div>

    <!-- PO 2026-08-29: the pressure block (เหลือ X วัน / ต้องทำ ฿X/วัน /
         วันที่) and the projection verdict line (ถ้าไปต่อที่อัตรานี้ →
         ฿X (ต่ำกว่าเป้า ✗)) were removed — the pace-gap callout + trend
         graph carry the same signal without the daily-burn pressure. -->
    <p v-if="brandView" class="target-pacing__brand-note">ยอดขายสะสมเดือนนี้ · เทียบสัปดาห์ก่อน</p>
    <p v-if="brandView && data.target_status === 'not_configured'" class="target-pacing__brand-note">ยังไม่ตั้งเป้าหมายแบรนด์</p>
    <SalesMixBar v-if="!brandView && mix" :mix="mix" />

    <!-- MTD discount (absorbed from the retired SalesWidget, tl5y) -->
    <p v-if="mtdDiscount" class="target-pacing__discount">{{ mtdDiscount }}</p>

    <!-- Cumulative actual vs target pace (uPlot, tl5y) -->
    <div
      v-if="paceSpec"
      class="target-pacing__chart"
      role="img"
      :aria-label="`กราฟยอดขายสะสมเทียบเป้าตามจังหวะ ปัจจุบันทำได้ ${fmtPct(progressPct)}% ของเป้าหมาย${paceGap ? ' · ' + paceGap.label : ''}`"
    >
      <!-- End-of-line gap callout (PO 2026-08-19) — the parallel-lines gap
           is only a few pixels tall; this states it in words. -->
      <span v-if="paceGap" class="target-pacing__gap" :class="paceGap.cls">{{ paceGap.label }}</span>
      <div ref="plotHost" class="target-pacing__plot"></div>
    </div>
  </section>
</template>

<style scoped>
.target-pacing {
  margin-bottom: 1rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  padding: 0.85rem;
  box-shadow: none;
  border-top: 3px solid var(--sales);
}

.target-pacing--on-track {
  border-top-color: var(--ok);
}

.target-pacing--off-track {
  border-top-color: var(--brand-light);
}

/* ── Header: global .card-head (canonical card title, 2026-08-18) ──────── */

/* ── Headline ───────────────────────────────────────────────────────────── */
.target-pacing__headline {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.target-pacing__headline-baht {
  font-size: 1.7rem;
  font-weight: 700;
  font-family: 'Kanit', 'Sarabun', sans-serif;
  line-height: 1.2;
  color: var(--text);
}

.target-pacing__headline-target {
  font-size: 0.72rem;
  color: var(--text-muted);
  font-family: 'Sarabun', sans-serif;
  white-space: nowrap;
}

/* ── MTD remark + freshness (absorbed from the retired MTD card) ─────────── */
.target-pacing__mtd-remark {
  margin: -0.25rem 0 0.5rem 0;
  font-size: 0.72rem;
  color: var(--text-muted);
  font-family: 'Sarabun', sans-serif;
}

.target-pacing__per-branch {
  font-variant-numeric: tabular-nums;
}

.target-pacing__freshness {
  margin: -0.25rem 0 0.55rem 0;
  padding: 0.3rem 0.55rem;
  background: var(--surface-2);
  border-radius: var(--radius-badge);
  font-size: 0.64rem;
  font-weight: 600;
  color: var(--text-dim);
  font-family: 'Sarabun', sans-serif;
  display: inline-block;
}

/* ── Progress bar ───────────────────────────────────────────────────────── */
.target-pacing__bar-wrap {
  margin-bottom: 0.55rem;
}

.target-pacing__bar-track {
  position: relative;
  height: 0.65rem;
  background: var(--surface-2);
  border-radius: var(--radius-badge);
  overflow: visible;
}

.target-pacing__bar-fill {
  height: 100%;
  background: var(--sales); /* solid gold — PO 2026-08-29: no gradient */
  border-radius: var(--radius-badge);
  min-width: 0;
  transition: width 0.5s ease;
}

.target-pacing__bar-proj {
  position: absolute;
  top: -0.15rem;
  height: calc(100% + 0.3rem);
  width: 2px;
  background: var(--color-bad);
  border-radius: var(--radius-badge);
}

.target-pacing__bar-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.58rem;
  color: var(--text-muted);
  margin-top: 0.15rem;
  font-family: 'Sarabun', sans-serif;
}

/* ── MTD discount + chart (tl5y) ─────────────────────────────────────────── */
.target-pacing__discount {
  margin: 0.35rem 0 0;
  padding: 0.4rem 0.55rem;
  background: var(--surface-2);
  border-radius: var(--radius-badge);
  font-size: 0.68rem;
  color: var(--text-dim);
  font-family: 'Sarabun', sans-serif;
  font-variant-numeric: tabular-nums;
}

.target-pacing__chart {
  position: relative;
  margin-top: 0.6rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--line);
}

/* End-of-line gap callout chip (PO 2026-08-19). */
.target-pacing__gap {
  position: absolute;
  top: 0.6rem;
  right: 0;
  z-index: 10;
  padding: 0.2rem 0.5rem;
  border-radius: var(--radius-badge);
  font-size: 0.64rem;
  font-weight: 700;
  font-family: 'Sarabun', sans-serif;
  font-variant-numeric: tabular-nums;
}

.target-pacing__gap--up {
  color: var(--ok);
  background: var(--ok-soft);
}

.target-pacing__gap--down {
  color: var(--color-bad);
  background: var(--brand-soft);
}

.target-pacing__gap--flat {
  color: var(--text-muted);
  background: var(--surface-2);
}

.target-pacing__plot {
  width: 100%;
}

/* ── Shared ─────────────────────────────────────────────────────────────── */
.state-msg {
  padding: 1rem 0.5rem;
  font-family: 'Sarabun', sans-serif;
  color: var(--text-dim);
  font-size: 0.82rem;
}
</style>
